import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useUser, useUserRoles } from "../authentication/authentication";
import api from "../api";
import translationData from "../pages/PRICING.json";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  DollarSign,
  RefreshCw,
  ArrowRight,
  User,
  Accessibility,
  HeartPulse,
  Receipt,
  ScanHeart,
  SquareActivity,
  Microscope,
  DatabaseBackup,
  BadgeCheck,
  CheckIcon,
  XIcon,
  ReceiptText,
  ShieldCheck,
  ShieldX,
  Home,
} from "lucide-react";
import { FaCalendarAlt } from "react-icons/fa";
import { arClass } from "../utils/useArClass";
import textTranslator from "../utils/useTextTranslator";
import typeTranslator from "../utils/useTypeTranslator";
import Txt from "../components/Text";
import formatDate from "../utils/formatDate";
import useWindowSize from "../utils/useWindowSize";
import { set } from "lodash";
import { useDispatch } from "react-redux";
import { toggleLanguage } from "../redux/settings/settingsActions";
import { NavLink } from "react-router-dom";
import { isAdminRole } from "../utils/adminChecker";
import { is } from "zod/v4/locales";

// Helper component for animated values
const AnimatedValue = ({
  value,
  placeholderWidth,
  bgColor,
  classNameValue = "",
  enablePulse = false,
  isCurrency = false,
  darkMode = false,
}) => {
  if (value === undefined || value === null) {
    return (
      <div
        className={`${placeholderWidth} h-6 ${bgColor} rounded animate-pulse ${
          darkMode ? "bg-gray-700" : "bg-gray-200"
        }`}
      ></div>
    );
  }

  return (
    <motion.span
      className={classNameValue}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {isCurrency && typeof value === "number" ? value.toFixed(2) : value}
    </motion.span>
  );
};

// Placeholder component
const Placeholder = ({ width, darkMode = false }) => (
  <div
    className={`${width} h-6 ${
      darkMode ? "bg-gray-700" : "bg-gray-200"
    } rounded animate-pulse`}
  ></div>
);

const Text = ({ children, className, page = "SUBSCRIPTION" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};
// Subscription Card Component (Integrated from your reference)
const SubscriptionCard = ({
  userSubscription,
  nextPayment,
  lastInvoice,
  darkMode = false,
}) => {
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );

  // Helper function to get translated plan name
  const getTranslatedPlanName = (planKey) => {
    if (!planKey || !translationData) return planKey;
    const plans = translationData[defaultLanguage]?.plans;
    if (!plans) return planKey;

    const planMap = {
      Free: "free",
      Starter: "starter",
      Pro: "pro",
      Enterprise: "enterprise",
    };

    const key = planMap[planKey] || planKey.toLowerCase();
    return plans[key]?.name || planKey;
  };

  // Helper function to get translated plan description
  const getTranslatedPlanDescription = (planKey) => {
    if (!planKey || !translationData) return null;
    const plans = translationData[defaultLanguage]?.plans;
    if (!plans) return null;

    const planMap = {
      Free: "free",
      Starter: "starter",
      Pro: "pro",
      Enterprise: "enterprise",
    };

    const key = planMap[planKey] || planKey.toLowerCase();
    return plans[key]?.description || null;
  };

  // Get translated billing period
  const getTranslatedBillingPeriod = (period) => {
    if (!period || !translationData?.meta[defaultLanguage]?.billingCycle)
      return period;

    const billingCycle = translationData.meta[defaultLanguage].billingCycle;
    const periods = {
      monthly: billingCycle[0] || "monthly",
      yearly: billingCycle[1] || "yearly",
    };

    return periods[period.toLowerCase()] || period;
  };

  // Get currency from translation data
  const getCurrency = () => {
    return translationData?.meta[defaultLanguage]?.currency || "MAD";
  };

  // Normalize limits to handle missing keys and null = unlimited representation
  const normalizeLimits = (limits = {}) => {
    const pick = (keyA, keyB, fallback = 0) => {
      const v = limits?.[keyA] ?? limits?.[keyB];
      if (v === null) return "∞";
      return v ?? fallback;
    };

    return {
      maxUsers: pick("maxUsers", "maxUsers", 0),
      maxPatientsPerMonth: pick("maxPatientsPerMonth", "maxPatients", 0),
      maxAppointmentsPerMonth: pick(
        "maxAppointmentsPerMonth",
        "maxAppointments",
        0,
      ),
      maxVisitsPerMonth: pick("maxVisitsPerMonth", "maxVisits", 0),
      maxPrescriptions: pick("maxPrescriptions", "maxPrescriptions", 0),
      maxLabTests: pick("maxLabTests", "maxLabTests", 0),
      maxInvoices: pick("maxInvoices", "maxInvoices", 0),
      maxPayments: pick("maxPayments", "maxPayments", 0),
      maxMedicalReports: pick("maxMedicalReports", "maxMedicalReports", 0),
      storageGBPerMonth: pick("storageGBPerMonth", "storageGB", 0),
      devicesPerUser: pick("devicesPerUser", "devicesPerUser", 0),
      loginHistoryDays: pick("loginHistoryDays", "loginHistoryDays", 0),
    };
  };

  // Returns translated features for the given features object and planName
  const getTranslatedFeatures = (featuresObj = {}, planName) => {
    if (!featuresObj) return [];
    const planKey =
      {
        Free: "free",
        Starter: "starter",
        Pro: "pro",
        Enterprise: "enterprise",
      }[planName] || (planName || "").toLowerCase();

    return Object.entries(featuresObj).map(([featureKey, featureObj]) => {
      const translated =
        translationData?.[defaultLanguage]?.plans?.[planKey]?.features?.[
          featureKey
        ];

      // Extract the actual value from the feature object
      const featureValue =
        typeof featureObj === "object" ? featureObj.value : featureObj;

      const label =
        translated?.name ||
        featureKey
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
      const description = translated?.description || "";

      return { key: featureKey, label, description, value: featureValue };
    });
  };

  // Precompute normalized limits
  const normalizedLimits = normalizeLimits(userSubscription?.limits);

  return (
    <motion.div
      className={arClass(
        `rounded-lg overflow-hidden border hover:shadow-sm transition-all duration-300 ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
        }`,
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div
        className={arClass(
          "bg-gradient-to-r from-blue-600 to-indigo-700 p-6 flex items-center justify-between relative overflow-hidden max-sm:p-3",
        )}
      >
        <motion.div
          className={arClass("absolute inset-0 opacity-10")}
          animate={{
            background: [
              "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
              "radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)",
              "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
            ],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />

        <span
          className={arClass("text-2xl font-bold text-white relative z-10")}
        >
          <AnimatedValue
            value={getTranslatedPlanName(userSubscription?.planName)}
            placeholderWidth="w-32"
            bgColor="bg-white/20"
            classNameValue="text-white backdrop-blur-sm"
            darkMode={darkMode}
          />
        </span>

        {userSubscription ? (
          userSubscription.isActive ? (
            <motion.span
              className={arClass(
                "px-4 py-2 text-sm font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 relative z-10 max-sm:text-xs",
              )}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
            >
              <Text>subscriptionActive</Text>
            </motion.span>
          ) : (
            <motion.span
              className={arClass(
                "px-4 py-2 text-sm font-semibold rounded-full bg-rose-100 text-rose-800 border border-rose-200 relative z-10 max-sm:text-xs",
              )}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
            >
              <Text>subscriptionInactive</Text>
            </motion.span>
          )
        ) : (
          <Placeholder width="w-20" darkMode={darkMode} />
        )}
      </div>

      {/* Content */}
      <div
        className={`p-6 flex flex-col gap-6 max-sm:p-1 ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Description */}
        <motion.div
          className={arClass(
            `flex items-center gap-3 rounded-xl p-4 border ${
              darkMode
                ? "bg-blue-900/20 text-gray-300 border-blue-800"
                : "bg-blue-50 text-gray-600 border-blue-100"
            }`,
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <BadgeCheck className="text-blue-600 text-xl flex-shrink-0" />
          <div className={arClass("text-sm leading-relaxed")}>
            <AnimatedValue
              value={
                getTranslatedPlanDescription(userSubscription?.planName) ||
                userSubscription?.description ||
                "No plan description available"
              }
              placeholderWidth="w-48"
              bgColor={darkMode ? "bg-gray-700" : "bg-white"}
              darkMode={darkMode}
            />
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          {/* Users */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-blue-900/20 to-indigo-900/20 border-blue-800"
                  : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100"
              }`,
            )}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <User className={arClass("text-blue-600 text-lg flex-shrink-0")} />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Users</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-blue-400" : "text-blue-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxUsers}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Patients */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border-emerald-800"
                  : "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-100"
              }`,
            )}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Accessibility
              className={arClass("text-emerald-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Patients</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-emerald-400" : "text-emerald-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxPatientsPerMonth}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Appointments */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-800"
                  : "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-100"
              }`,
            )}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35 }}
          >
            <Calendar
              className={arClass("text-purple-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Appointments</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-purple-400" : "text-purple-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxAppointmentsPerMonth}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Visits */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-amber-900/20 to-orange-900/20 border-amber-800"
                  : "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-100"
              }`,
            )}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <HeartPulse
              className={arClass("text-amber-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Visits</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-amber-400" : "text-amber-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxVisitsPerMonth}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Payments */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-teal-900/20 to-cyan-900/20 border-teal-800"
                  : "bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-100"
              }`,
            )}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Receipt
              className={arClass("text-teal-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Payments</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-teal-400" : "text-teal-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxPayments}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Medical Reports */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-pink-900/20 to-rose-900/20 border-pink-800"
                  : "bg-gradient-to-r from-pink-50 to-rose-50 border-pink-100"
              }`,
            )}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.55 }}
          >
            <ScanHeart
              className={arClass("text-pink-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Medical Reports</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-pink-400" : "text-pink-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxMedicalReports}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Prescriptions */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border-yellow-800"
                  : "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-100"
              }`,
            )}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <SquareActivity
              className={arClass("text-yellow-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Prescriptions</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-yellow-400" : "text-yellow-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxPrescriptions}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Lab Tests */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border ${
                darkMode
                  ? "bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border-cyan-800"
                  : "bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-100"
              }`,
            )}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.65 }}
          >
            <Microscope
              className={arClass("text-cyan-600 text-lg flex-shrink-0")}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Lab Tests</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-cyan-400" : "text-cyan-700"
                }`}
                bgColor={darkMode ? "bg-gray-700/80" : "bg-white/80"}
                value={normalizedLimits.maxLabTests}
                enablePulse={true}
                darkMode={darkMode}
              />
            </div>
          </motion.div>

          {/* Storage */}
          <motion.div
            className={arClass(
              `flex items-center gap-3 p-3 rounded-lg border col-span-2 ${
                darkMode
                  ? "bg-gradient-to-r from-gray-700 to-slate-700 border-gray-600"
                  : "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-100"
              }`,
            )}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <DatabaseBackup
              className={arClass(
                `text-lg flex-shrink-0 ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`,
              )}
            />
            <div
              className={arClass("flex items-center justify-between w-full")}
            >
              <span
                className={arClass(
                  `font-semibold ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`,
                )}
              >
                <Text>Storage</Text>
              </span>
              <AnimatedValue
                classNameValue={`font-bold ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
                bgColor={darkMode ? "bg-gray-600/80" : "bg-white/80"}
                value={
                  userSubscription
                    ? `${normalizedLimits.storageGBPerMonth || 0} GB`
                    : null
                }
                placeholderWidth="w-28"
                darkMode={darkMode}
              />
            </div>
          </motion.div>
        </div>

        {/* Price Section */}
        <motion.div
          className={arClass(
            `flex items-center justify-between mt-4 p-4 rounded-xl border ${
              darkMode
                ? "bg-gradient-to-r from-slate-700 to-gray-700 border-gray-600"
                : "bg-gradient-to-r from-slate-50 to-gray-50 border-gray-200"
            }`,
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className={arClass("flex items-baseline gap-2")}>
            <span
              className={arClass(
                `text-sm ${darkMode ? "text-gray-100" : "text-gray-900"}`,
              )}
            >
              <AnimatedValue
                value={
                  userSubscription
                    ? defaultLanguage !== "ar"
                      ? `${getCurrency()} ${userSubscription.price}`
                      : `${userSubscription.price} ${getCurrency()}`
                    : null
                }
                placeholderWidth="w-32"
                bgColor={darkMode ? "bg-gray-700" : "bg-white"}
                isCurrency={true}
                darkMode={darkMode}
              />
            </span>
            <span
              className={arClass(
                `text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`,
              )}
            >
              /
              <AnimatedValue
                value={getTranslatedBillingPeriod(
                  userSubscription?.billingPeriod,
                )}
                placeholderWidth="w-16"
                bgColor="bg-transparent"
                darkMode={darkMode}
              />
            </span>
          </div>
          {userSubscription?.isRecommended && (
            <motion.span
              className={arClass(
                "px-4 py-2 text-sm font-semibold rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg max-sm:text-xs",
              )}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              ⭐ <Text>recommended</Text>
            </motion.span>
          )}
        </motion.div>

        {/* Next Payment Section */}
        {nextPayment && userSubscription.isActive && (
          <motion.div
            className={arClass(
              `mt-4 p-4 rounded-xl border ${
                darkMode
                  ? "bg-gradient-to-r from-green-900/20 to-emerald-900/20 border-green-800"
                  : "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
              }`,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <div
              className={arClass(
                `flex gap-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`,
              )}
            >
              <FaCalendarAlt className="text-green-600 text-lg flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold">
                  <Text>Next Payment</Text>
                </div>
                <div
                  className={`text-xs ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <Text>Due</Text>: {formatDate(nextPayment.nextPaymentDate)} (
                  {getTranslatedBillingPeriod(nextPayment.billingPeriod)})
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Last Payment Section */}
        {lastInvoice && userSubscription && (
          <motion.div
            className={arClass(
              `mt-4 p-4 rounded-xl border ${
                darkMode
                  ? "bg-gradient-to-r from-green-900/20 to-emerald-900/20 border-green-800"
                  : "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
              }`,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <div
              className={arClass(
                `flex gap-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`,
              )}
            >
              <ReceiptText
                className={arClass("text-blue-600 text-lg flex-shrink-0")}
              />
              <div>
                <div className={arClass("text-sm font-semibold")}>
                  <Text>Last Payment</Text>
                </div>
                <div
                  className={arClass(
                    `text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`,
                  )}
                >
                  <Text>Period of</Text> {formatDate(lastInvoice.issueDate)} -{" "}
                  {formatDate(lastInvoice.periodEnd)} -{" "}
                  <Text page="PAYMENTS">{lastInvoice.status}</Text>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Features List */}
        {userSubscription?.features &&
        Object.keys(userSubscription.features).length > 0 ? (
          <motion.ul
            className={arClass(
              `mt-4 grid grid-cols-2 text-sm rounded-xl p-4 border gap-2 max-sm:grid-cols-1 ${
                darkMode
                  ? "bg-gradient-to-r from-blue-900/20 to-indigo-900/20 text-gray-300 border-blue-800"
                  : "bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-600 border-blue-100"
              }`,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {(() => {
              const translatedFeatures = getTranslatedFeatures(
                userSubscription.features,
                userSubscription?.planName,
              );
              const featuresToShow = translatedFeatures;

              return (
                <>
                  {featuresToShow.map((f, idx) => (
                    <motion.li
                      key={idx}
                      className={arClass("flex gap-2")}
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + idx * 0.05 }}
                    >
                      {f.value === true || f.value !== false ? (
                        <CheckIcon className="w-4 h-4 text-green-500" />
                      ) : (
                        <XIcon className="w-4 h-4 text-rose-500" />
                      )}
                      <div>
                        <span className={arClass("truncate")}>{f.label}</span>
                        {f.description && (
                          <div
                            className={arClass(
                              `text-xs mt-0.5 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`,
                            )}
                          >
                            {f.description}
                          </div>
                        )}
                      </div>
                    </motion.li>
                  ))}
                </>
              );
            })()}
          </motion.ul>
        ) : null}
      </div>
    </motion.div>
  );
};

const Navbar = ({ darkMode = false, userSubscription }) => {
  const user = useUser();
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const dispatch = useDispatch();
  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    // Dispatch action to update language in Redux store
    dispatch(toggleLanguage(selectedLanguage));
  };

  const selectedLanguageOptions = [
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "ar", label: "العربية" },
  ];
  return (
    <nav
      className={arClass(
        `w-full py-4 px-6 flex items-center justify-between border-b max-sm:flex-col max-sm:items-start max-sm:justify-start max-sm:gap-4 max-sm:p-3 ${
          darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
        }`,
      )}
    >
      <div
        className={arClass(
          "flex items-center gap-6 max-sm:w-full max-sm:justify-between flex-[3]",
        )}
      >
        <h1
          className={arClass(
            `text-xl font-bold max-sm:text-sm ${
              darkMode ? "text-white" : "text-gray-900"
            }`,
          )}
        >
          <Text>Subscription Management</Text>
        </h1>
        <div
          className={arClass(
            "hidden max-sm:flex max-sm:items-center max-sm:gap-2",
          )}
        >
          <img
            src={user?.profileImage || ""}
            alt={user?.name || "User"}
            className="w-8 h-8 rounded-full"
          />
          <span
            className={arClass(
              `text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`,
            )}
          >
            {user?.name || user?.username || "User"}
          </span>
        </div>
      </div>
      <div className={arClass("flex items-center gap-6 flex-1")}>
        <div className={arClass("flex items-center gap-4 flex-1 w-max")}>
          <NavLink
            to={userSubscription.isActive ? "/dashboard" : "/subscription"}
            className={arClass(
              `flex gap-2 px-3 py-1.5 items-center border rounded-full max-sm:text-xs w-max ${
                darkMode
                  ? "border-gray-600 text-gray-200"
                  : "bg-white border-gray-300 text-gray-900"
              } ${
                userSubscription.isActive
                  ? ""
                  : "pointer-events-none opacity-50"
              }`,
            )}
          >
            <Home
              className={arClass(
                `w-4 h-4 sm:w-5 sm:h-5 ${
                  darkMode ? "text-white" : "text-gray-900"
                } ${
                  userSubscription.isActive
                    ? ""
                    : "pointer-events-none opacity-50 text-red-600"
                }`,
              )}
            />
            <span
              className={arClass(
                `max-sm:text-xs ${darkMode ? "text-white" : "text-gray-900"} ${
                  userSubscription.isActive
                    ? ""
                    : "pointer-events-none opacity-50 text-red-600"
                }`,
              )}
            >
              <Text>Dashboard</Text>
            </span>
          </NavLink>
        </div>
        <div className={arClass("flex items-center gap-4 flex-1 w-max")}>
          <select
            onChange={handleLanguageChange}
            value={defaultLanguage}
            className={arClass(
              `px-3 py-0.5 rounded-full  border max-sm:text-xs ${
                darkMode
                  ? "bg-gray-800 border-gray-600 text-gray-200"
                  : "bg-white border-gray-300 text-gray-900"
              }`,
            )}
          >
            {selectedLanguageOptions.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>
        <div
          className={arClass("flex items-center gap-2 flex-1 max-sm:hidden")}
        >
          <img
            src={user?.profileImage || ""}
            alt={user?.name || "User"}
            className="w-8 h-8 rounded-full"
          />
          <span
            className={arClass(
              `text-sm w-max ${darkMode ? "text-gray-300" : "text-gray-700"}`,
            )}
          >
            {user?.name || user?.username || "User"}
          </span>
        </div>
      </div>
    </nav>
  );
};

const Spinner = ({ darkMode = false }) => {
  return (
    <div
      className={arClass(
        `flex items-center justify-center p-6 ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`,
      )}
    >
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
    </div>
  );
};
// Main Unpaid Component
const Subscription = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [nextPayment, setNextPayment] = useState(null);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [planToShow, setPlanToShow] = useState("free");

  const roles = useUserRoles();
  const [defaultPlan, setDefaultPlan] = useState({});


  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const user = useUser();

  // Get translations from JSON
  const t =
    translationData?.[defaultLanguage]?.common ||
    translationData?.en?.common ||
    {};

  useEffect(() => {
    if (showPaymentModal && selectedPlan) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showPaymentModal, selectedPlan]);
  // Fetch user subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setLoadingSubscription(true);
        const res = await api.get(`users/subscription/`);

        setUserSubscription(res.data.plan);
        setNextPayment(res.data.nextPayment);
        setLastInvoice(res.data.lastInvoice);

        setLoadingSubscription(false);
      } catch (err) {
        setUserSubscription(null);
        setNextPayment(null);
        setLastInvoice(null);
      }
    };

    fetchSubscription();
  }, [user?.id]);

  // Plans data from PRICING.json
  const pricingPlansObj =
    translationData?.[defaultLanguage]?.plans ||
    translationData?.en?.plans ||
    {};

  const defaultEnPlanObj = translationData?.en?.plans
  const currency = translationData?.meta?.[defaultLanguage]?.currency || "MAD";
  const defaultCurrency = translationData?.meta?.en?.currency || "MAD";

  // Transform plans data for display
  const plans = Object.entries(pricingPlansObj).map(([key, p]) => {
    const price = p.price ?? null;
    const yearlyPrice = price ? Math.round(price * 12 * 0.9) : 0; // 10% yearly discount
    const features = Object.entries(p.features || {}).map(([fk, fv]) => ({
      name:
        fv?.name ||
        fk.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
      description: fv?.description || "",
      included: fv?.value === true || typeof fv?.value === "string",
      value: fv?.value,
    }));

    const limits = p.limits || {};

    return {
      id: key,
      name: p.name,
      price,
      yearlyPrice,
      features,
      limits,
      raw: p,
      popular: !!p.recommended,
      description: p.description,
    };
  });

  const enPlan = Object.entries(defaultEnPlanObj).map(([key, p]) => {
    const price = p.price ?? null;
    const yearlyPrice = price ? Math.round(price * 12 * 0.9) : 0; // 10% yearly discount
    const features = Object.entries(p.features || {}).map(([fk, fv]) => ({
      name:
        fv?.name || 
        fk.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
      description: fv?.description || "",
      included: fv?.value === true || typeof fv?.value === "string",
      value: fv?.value,
    }));
    const limits = p.limits || {};

    return {
      id: key,
      name: p.name,
      price,
      yearlyPrice,
      features,
      limits,
      raw: p,
      popular: !!p.recommended,
      description: p.description,
    };
  });

  // Auto-select the user's current plan (if exists in PRICING.json)
  useEffect(() => {
    if (!userSubscription) return;
    const planKey = (
      userSubscription.planName ||
      userSubscription?.plan?.planId ||
      userSubscription?.plan?.name ||
      ""
    )
      .toString()
      .toLowerCase();
    if (!planKey) return;
    const found = plans.find(
      (p) =>
        p.id.toString().toLowerCase() === planKey ||
        (p.name || "").toString().toLowerCase() === planKey,
    );
    
    
    if (found){
      setSelectedPlan(found)
  
    }
  }, [userSubscription, defaultLanguage]);


  const handlePlanSelect = (plan) => {
    const defaultFound = enPlan.find((p) => p.id === plan.id);
    setDefaultPlan(defaultFound || {});
    setSelectedPlan(plan);
  
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    setPaymentStatus("processing");

    try {
      const response = await api.post("it-requests", {
        requestType: "SUBSCRIPTION_REQUEST",
        title: `Plan Upgrade Request: ${defaultPlan?.name}`,
        description: `User is requesting to upgrade from ${userSubscription?.planName} to ${defaultPlan?.name} on a ${
          isYearly ? "yearly" : "monthly"
        } billing cycle`,
        priority: "HIGH",
        subscriptionDetails: {
          requestSubType: "UPGRADE",
          currentPlan: userSubscription?.planName,
          requestedPlan: defaultPlan?.name,
          billingCycle: isYearly ? "yearly" : "monthly",
          price: isYearly ? defaultPlan.yearlyPrice : defaultPlan.price,
          currency: defaultCurrency ,
          userEmail: user?.email,
          userName: user?.name || user?.username,
          clinicId: userSubscription?.clinicId,
          additionalNotes: document.querySelector("textarea")?.value || "",
        },
      });

      if (response.status === 201) {
        setPaymentStatus("success");
      
        setTimeout(() => {
       
          setShowPaymentModal(false);
          setPaymentStatus(null);
        }, 3000);
      } else {
        setPaymentStatus("error");
      }
    } catch (error) {
      setPaymentStatus("error");
      console.error("Error submitting upgrade request:", error);
    }
  };
  const handlePlanToShow = (planId) => {
    setPlanToShow(planId);
  };
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  const getTranslatedPlanName = (planKey) => {
    if (!planKey || !translationData) return planKey;
    const plans = translationData[defaultLanguage]?.plans;
    if (!plans) return planKey;

    const planMap = {
      Free: "free",
      Starter: "starter",
      Pro: "pro",
      Enterprise: "enterprise",
    };

    const key = planMap[planKey] || planKey.toLowerCase();
    return plans[key]?.name || planKey;
  };

  const renderPlan = () => {
    const plan = plans.find(
      (p) => p.id === planToShow && !(isYearly && planToShow === "free"),
    );
    return (
      <motion.div
        variants={itemVariants}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        whileHover={{ y: -5 }}
        className={`relative rounded-2xl p-6 ${
          darkMode ? "bg-gray-800" : "bg-white"
        } shadow-lg ring ${
          plan.popular
            ? "ring-blue-500"
            : darkMode
              ? " ring-gray-400"
              : " ring-gray-300"
        } ${selectedPlan?.id === plan.id ? " !ring-blue-500" : ""}`}
      >
        {plan.popular && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="px-4 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
              <Text>recommended</Text>
            </span>
          </div>
        )}

        <div className="mb-6">
          <h3 className={arClass("text-lg font-bold mb-2")}>{plan.name}</h3>
          <p
            className={arClass(
              `text-xs mb-4 ${darkMode ? "text-gray-400" : "text-gray-600"}`,
            )}
          >
            {plan.description}
          </p>
          <div className={arClass("flex items-baseline mb-4")}>
            <span className={arClass("text-lg font-bold")}>
              {plan.price !== null ? (
                `${isYearly ? plan.yearlyPrice : plan.price} ${currency}`
              ) : (
                <Text>Custom</Text>
              )}
            </span>
            <span
              className={arClass(
                `mx-2 text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`,
              )}
            >
              {plan.id === "free" ? (
                <Text>/7 Days Free Trial</Text>
              ) : (
                <>
                  {plan.price !== null ? (
                    isYearly ? (
                      <Text>/year</Text>
                    ) : (
                      <Text>/month</Text>
                    )
                  ) : (
                    <Text>Custom</Text>
                  )}
                </>
              )}
            </span>
          </div>

          {isYearly && plan.price && plan.yearlyPrice && (
            <div
              className={arClass(
                `text-xs ${darkMode ? "text-green-400" : "text-green-600"}`,
              )}
            >
              <Text>Save</Text>{" "}
              <span className={arClass("text-xs")}>
                {(plan.price * 12 - plan.yearlyPrice).toFixed(0)}
              </span>{" "}
              <span className={arClass("text-xs")}>{currency}</span>
            </div>
          )}
        </div>

        <div className={arClass("flex flex-col gap-2")}>
          {plan.features.map((feature, idx) => (
            <div key={idx} className={arClass("p-1")}>
              <div className={arClass("flex gap-2")}>
                {feature.included ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 mt-1" />
                )}
                <div>
                  <div
                    className={`${
                      !feature.included
                        ? arClass("opacity-50 text-xs")
                        : arClass("font-medium text-xs")
                    }`}
                  >
                    {feature.name}
                    {feature.value !== undefined &&
                      feature.value !== null &&
                      typeof feature.value !== "boolean" &&
                      String(feature.value).trim() !== "" && (
                        <span className={arClass("font-semibold text-xs")}>
                          {" "}
                          ({feature.value})
                        </span>
                      )}
                  </div>
                  {feature.description && (
                    <div
                      className={arClass(
                        `text-xsm ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`,
                      )}
                    >
                      {feature.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handlePlanSelect(plan)}
          className={`w-full py-3 rounded-lg font-semibold transition-colors text-xs mt-2 ${
            plan.popular ||
            selectedPlan?.id === plan.id ||
            planToShow === plan.id
              ? "!bg-blue-500 !hover:bg-blue-600 text-white"
              : darkMode
                ? "bg-gray-700 hover:bg-gray-600 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-900"
          }`}
        >
          {/* Show Current if it's the user's active plan */}
          <Text>
            {userSubscription &&
            (
              userSubscription.planName ||
              userSubscription?.plan?.planId ||
              userSubscription?.plan?.name ||
              ""
            )
              .toString()
              .toLowerCase() === plan.id.toString().toLowerCase()
              ? "Current Plan"
              : selectedPlan?.id === plan.id
                ? "Selected Plan"
                : "Choose a Plan"}
          </Text>
        </motion.button>
      </motion.div>
    );
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      } transition-colors duration-300`}
    >
      {userSubscription ? (
        <Navbar darkMode={darkMode} userSubscription={userSubscription} />
      ) : (
        <Navbar darkMode={darkMode} userSubscription={{ isActive: false }} />
      )}
      <div className="w-full px-4 py-12 max-sm:px-3">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-12"
        >
          <motion.div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${
              userSubscription && userSubscription.isActive
                ? darkMode
                  ? "bg-green-800/30"
                  : "bg-green-100"
                : darkMode
                  ? "bg-red-800/30"
                  : "bg-red-100"
            } shadow-lg`}
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0],
            }}
            transition={{ repeat: Infinity, duration: 4 }}
          >
            {loadingSubscription ? (
              <Spinner
                className={arClass(
                  `text-green-600 w-8 h-8 animate-spin ${
                    darkMode ? "text-green-400" : "text-green-600"
                  }`,
                )}
              />
            ) : userSubscription && userSubscription.isActive ? (
              <ShieldCheck
                className={arClass(
                  `text-green-600 w-8 h-8 ${
                    darkMode ? "text-green-400" : "text-green-600"
                  }`,
                )}
              />
            ) : (
              <ShieldX
                className={arClass(
                  `text-red-600 w-8 h-8 ${
                    darkMode ? "text-red-400" : "text-red-600"
                  }`,
                )}
              />
            )}
          </motion.div>

          <h1 className="text-4xl font-bold mb-4 max-sm:text-sm">
            {loadingSubscription ? (
              t.loadingSubscription || "Loading Subscription"
            ) : userSubscription ? (
              userSubscription.isActive ? (
                <Text>activePlaneTitle</Text>
              ) : (
                <Text>expiredPlaneTitle</Text>
              )
            ) : (
              <Text>unactivePlaneTitle</Text>
            )}
          </h1>
          <p
            className={`text-lg max-sm:text-xs ${
              darkMode ? "text-gray-300" : "text-gray-600"
            } max-w-2xl mx-auto`}
          >
            {loadingSubscription ? (
              t.loadingSubscriptionDetails ||
              "Loading your subscription details..."
            ) : (
              <>
                {userSubscription ? (
                  userSubscription.isActive ? (
                    <Text>activePlaneDesc</Text>
                  ) : (
                    <Text>expiredPlaneDesc</Text>
                  )
                ) : (
                  <Text>unactivePlaneDesc</Text>
                )}
              </>
            )}
          </p>
        </motion.div>

        {/* Current Subscription Card */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-12"
        >
          <SubscriptionCard
            userSubscription={userSubscription}
            nextPayment={nextPayment}
            lastInvoice={lastInvoice}
            loading={loadingSubscription}
            defaultLanguage={defaultLanguage}
            darkMode={darkMode}
          />
        </motion.div>

        {/* Plan Not Found Warning */}
        {userSubscription &&
          !pricingPlansObj[userSubscription.planName?.toLowerCase()] && (
            <motion.div
              variants={itemVariants}
              className={`mb-6 p-4 rounded-lg border ${
                darkMode
                  ? "bg-rose-900/10 border-rose-800 text-rose-200"
                  : "bg-rose-50 border-rose-100 text-rose-700"
              }`}
            >
              <div className="font-semibold max-sm:text-xs">
                Plan Configuration Needed
              </div>
              <div className="text-sm max-sm:text-xs">
                Your current plan "{userSubscription.planName}" isn't configured
                in the pricing system. Please choose a new plan below or contact
                support.
              </div>
            </motion.div>
          )}

        {
          isAdminRole(roles) && (
            <>
            {/* Available Plans */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-center mb-8 max-sm:text-lg">
            {t.selectPlan || "Select a Plan"}
          </h2>
          {/* Billing Toggle */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-12"
          >
            <div
              className={`inline-flex p-1 rounded-xl ${
                darkMode ? "bg-gray-800" : "bg-gray-200"
              }`}
            >
              <button
                onClick={() => {
                  setIsYearly(false);
                  setPlanToShow("free");
                }}
                className={`px-3 py-3 rounded-lg font-medium transition-all max-sm:text-xs max-sm:p-2 ${
                  !isYearly
                    ? `${darkMode ? "bg-gray-700" : "bg-white"} shadow-lg`
                    : ""
                }`}
              >
                <Text>Monthly</Text>
              </button>
              <button
                onClick={() => {
                  setIsYearly(true);
                  setPlanToShow("starter");
                }}
                className={`px-3 py-3 rounded-lg font-medium transition-all max-sm:text-xs max-sm:p-2 ${
                  isYearly
                    ? `${darkMode ? "bg-gray-700" : "bg-white"} shadow-lg`
                    : ""
                }`}
              >
                <Text>Yearly (Save 10%)</Text>
              </button>
            </div>
          </motion.div>
          {isMobile ? (
            <div
              className={arClass("flex flex-col max-sm:justify-center my-4")}
            >
              <nav className={arClass("flex justify-center mb-10")}>
                {plans
                  .filter((plan) => !(isYearly && plan.id === "free"))
                  .map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => handlePlanToShow(plan.id)}
                      className={arClass(
                        `mx-2 rounded-full font-medium transition-colors text-xs p-2 flex-1 shadow-lg duration-[500ms] ${
                          darkMode
                            ? "bg-blue-700 hover:bg-blue-600 text-white"
                            : "bg-blue-100 hover:bg-blue-300 text-blue-900"
                        }
                                ${
                                  planToShow === plan.id
                                    ? darkMode
                                      ? "!bg-blue-700 !text-blue-100"
                                      : "!bg-blue-600 !text-blue-100"
                                    : ""
                                }`,
                      )}
                    >
                      {plan.name}
                    </button>
                  ))}
              </nav>
              {renderPlan()}
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              className={arClass(
                `grid  gap-6 ${
                  isYearly
                    ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 "
                    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                }`,
              )}
            >
              {plans
                .filter((plan) => !(isYearly && plan.id === "free"))
                .map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    variants={itemVariants}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className={`relative rounded-2xl p-6 ${
                      darkMode ? "bg-gray-800" : "bg-white"
                    } shadow-lg border-2 ${
                      plan.popular
                        ? "border-blue-500"
                        : darkMode
                          ? " border-gray-400"
                          : " border-gray-300"
                    } ${
                      selectedPlan?.id === plan.id ? "ring-2 ring-blue-500" : ""
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="px-4 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                          <Text>recommended</Text>
                        </span>
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className={arClass("text-xl font-bold mb-2")}>
                        {plan.name}
                      </h3>
                      <p
                        className={arClass(
                          `text-sm mb-4 ${
                            darkMode ? "text-gray-400" : "text-gray-600"
                          }`,
                        )}
                      >
                        {plan.description}
                      </p>
                      <div className={arClass("flex items-baseline mb-4")}>
                        <span className={arClass("text-4xl font-bold")}>
                          {plan.price !== null ? (
                            `${
                              isYearly ? plan.yearlyPrice : plan.price
                            } ${currency}`
                          ) : (
                            <Text>Custom</Text>
                          )}
                        </span>
                        <span
                          className={arClass(
                            `mx-2 ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`,
                          )}
                        >
                          {plan.price !== null ? (
                            isYearly ? (
                              <Text>/year</Text>
                            ) : (
                              <Text>/month</Text>
                            )
                          ) : (
                            <Text>Custom</Text>
                          )}
                        </span>
                      </div>

                      {isYearly && plan.price && plan.yearlyPrice && (
                        <div
                          className={arClass(
                            `text-sm ${
                              darkMode ? "text-green-400" : "text-green-600"
                            }`,
                          )}
                        >
                          <Text>Save</Text>{" "}
                          <span className={arClass("text-sm")}>
                            {(plan.price * 12 - plan.yearlyPrice).toFixed(0)}
                          </span>{" "}
                          <span className={arClass("text-sm")}>{currency}</span>
                        </div>
                      )}
                    </div>

                    <div className={arClass("flex flex-col gap-3")}>
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className={arClass("p-1")}>
                          <div className={arClass("flex gap-2")}>
                            {feature.included ? (
                              <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500 mt-1" />
                            )}
                            <div>
                              <div
                                className={`${
                                  !feature.included
                                    ? "opacity-50"
                                    : "font-medium"
                                }`}
                              >
                                {feature.name}
                                {feature.value !== undefined &&
                                  feature.value !== null &&
                                  typeof feature.value !== "boolean" &&
                                  String(feature.value).trim() !== "" && (
                                    <span className="font-semibold">
                                      {" "}
                                      ({feature.value})
                                    </span>
                                  )}
                              </div>
                              {feature.description && (
                                <div
                                  className={`text-xs ${
                                    darkMode ? "text-gray-400" : "text-gray-500"
                                  }`}
                                >
                                  {feature.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handlePlanSelect(plan)}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors mt-4 ${
                        plan.popular || selectedPlan?.id === plan.id
                          ? "!bg-blue-500 !hover:bg-blue-600 text-white"
                          : darkMode
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                      }`}
                      disabled={
                        userSubscription &&
                        userSubscription.isActive &&
                        (userSubscription.planName || "").toLowerCase() ===
                          plan.id.toString().toLowerCase()
                      }
                    >
                      {/* Show Current if it's the user's active plan */}
                      <Text>
                        {userSubscription &&
                        (
                          userSubscription.planName ||
                          userSubscription?.plan?.planId ||
                          userSubscription?.plan?.name ||
                          ""
                        )
                          .toString()
                          .toLowerCase() === plan.id.toString().toLowerCase()
                          ? "Current Plan"
                          : selectedPlan?.id === plan.id
                            ? "Selected Plan"
                            : "Choose a Plan"}
                      </Text>
                    </motion.button>
                  </motion.div>
                ))}
            </motion.div>
          )}
        </div>
            </>
          )
        }
      </div>
      {
        isAdminRole(roles) && (
          <AnimatePresence>
        {showPaymentModal && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 max-sm:p-0 "
            onClick={() => !paymentStatus && setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className={arClass(
                `w-8/12 rounded-2xl shadow-2xl h-[90vh] overflow-y-auto max-sm:w-full max-sm:h-[100vh] max-sm:rounded-none  ${
                  darkMode ? "bg-gray-800" : "bg-white"
                } overflow-hidden`,
              )}
            >
              {/* Modal Header */}
              <div
                className={arClass(
                  `p-6 ${darkMode ? "bg-gray-900" : "bg-gray-50"} border-b ${
                    darkMode ? "border-gray-700" : "border-gray-200"
                  }`,
                )}
              >
                <div className={arClass("flex items-center justify-between")}>
                  <h3 className={arClass("text-xl font-bold max-sm:text-sm")}>
                    <Text>Request Upgrade</Text>
                  </h3>
                  {!paymentStatus && (
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className={arClass(
                        `p-2 rounded-lg max-sm:text-xs ${
                          darkMode ? "hover:bg-gray-800" : "hover:bg-gray-200"
                        }`,
                      )}
                    >
                      <XCircle className={arClass("w-5 h-5")} />
                    </button>
                  )}
                </div>

                {/* Selected Plan */}
                <div
                  className={arClass(
                    "mt-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20",
                  )}
                >
                  <div className={arClass("flex items-center justify-between")}>
                    <div>
                      <h4 className={arClass("font-semibold max-sm:text-sm")}>
                        {selectedPlan.name}
                      </h4>
                      <p
                        className={arClass(
                          `text-sm max-sm:text-xs ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`,
                        )}
                      >
                        {isYearly ? <Text>Yearly</Text> : <Text>Monthly</Text>}
                        {isYearly &&
                          selectedPlan.price &&
                          selectedPlan.yearlyPrice && (
                            <div
                              className={arClass(
                                `text-xs ${
                                  darkMode ? "text-green-400" : "text-green-600"
                                }`,
                              )}
                            >
                              <Text>Save</Text>{" "}
                              <span className={arClass("text-xs")}>
                                {(
                                  selectedPlan.price * 12 -
                                  selectedPlan.yearlyPrice
                                ).toFixed(0)}
                              </span>{" "}
                              <span className={arClass("text-xs")}>
                                {currency}
                              </span>
                            </div>
                          )}
                      </p>
                    </div>
                    <div className={arClass("text-right")}>
                      <div
                        className={arClass("text-2xl font-bold max-sm:text-sm")}
                      >
                        {selectedPlan.price !== null ? (
                          defaultLanguage !== "ar" ? (
                            `${currency} ${
                              isYearly
                                ? selectedPlan.yearlyPrice
                                : selectedPlan.price
                            }`
                          ) : (
                            `${
                              isYearly
                                ? selectedPlan.yearlyPrice
                                : selectedPlan.price
                            } ${currency}`
                          )
                        ) : (
                          <Text>Custom</Text>
                        )}
                      </div>
                      <div
                        className={arClass(
                          `text-sm max-sm:text-xs ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`,
                        )}
                      >
                        {isYearly ? <Text>/year</Text> : <Text>/month</Text>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className={arClass("p-6 max-sm:p-4")}>
                {paymentStatus === "success" ? (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={arClass("text-center py-8")}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                      className={arClass(
                        "w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6",
                      )}
                    >
                      <CheckCircle className={arClass("w-8 h-8 text-white")} />
                    </motion.div>
                    <h4
                      className={arClass(
                        "text-2xl font-bold mb-2 max-sm:text-sm",
                      )}
                    >
                      <Text>Request Sent!</Text>
                    </h4>
                    <p
                      className={arClass(
                        darkMode
                          ? "text-gray-400 max-sm:text-xs"
                          : "text-gray-600 max-sm:text-xs",
                      )}
                    >
                      <Text>
                        We've received your upgrade request. Our team will
                        contact you shortly to complete the process.
                      </Text>
                    </p>
                    <div
                      className={arClass("mt-4 p-3 bg-green-50  rounded-lg")}
                    >
                      <p
                        className={arClass(
                          "text-sm max-sm:text-xs text-green-700",
                        )}
                      >
                        <CheckCircle
                          className={arClass("w-4 h-4 inline mr-2")}
                        />

                        <Text>
                          Confirmation email has been sent to your registered
                          email address.
                        </Text>
                      </p>
                    </div>
                  </motion.div>
                ) : paymentStatus === "failed" ? (
                  <div className={arClass("text-center py-8")}>
                    <div
                      className={arClass(
                        "w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6",
                      )}
                    >
                      <XCircle className={arClass("w-8 h-8 text-white")} />
                    </div>
                    <h4
                      className={arClass(
                        "text-2xl font-bold mb-2 max-sm:text-sm",
                      )}
                    >
                      <Text>Request Failed</Text>
                    </h4>
                    <p
                      className={arClass(
                        `mb-6 ${
                          darkMode
                            ? "text-gray-400 max-sm:text-xs"
                            : "text-gray-600 max-sm:text-xs"
                        }`,
                      )}
                    >
                      <Text>
                        Unfortunately, there was an issue sending your upgrade
                        request. Please try again later.
                      </Text>
                    </p>
                    <button
                      onClick={() => setPaymentStatus(null)}
                      className={arClass(
                        "px-6 py-3 bg-red-500 text-white rounded-lg font-medium max-sm:px-4 max-sm:py-2 max-sm:text-xs",
                      )}
                    >
                      <Text>Try Again</Text>
                    </button>
                  </div>
                ) : paymentStatus === "processing" ? (
                  <div className={arClass("text-center py-12")}>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        repeat: Infinity,
                        duration: 1,
                        ease: "linear",
                      }}
                      className={arClass(
                        "w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-6",
                      )}
                    />
                    <h4
                      className={arClass(
                        "text-xl font-bold mb-2 max-sm:text-sm",
                      )}
                    >
                      <Text>Processing Request...</Text>
                    </h4>
                    <p
                      className={arClass(
                        darkMode
                          ? "text-gray-400 max-sm:text-xs"
                          : "text-gray-600 max-sm:text-xs",
                      )}
                    >
                      <Text>
                        Please wait while we process your upgrade request.
                      </Text>
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Request Form - Instead of Payment Form */}
                    <div className={arClass("flex flex-col gap-6")}>
                      <div className={arClass("text-center")}>
                        <div
                          className={arClass(
                            "w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4",
                          )}
                        >
                          <svg
                            className={arClass(
                              "w-8 h-8 text-blue-600 dark:text-blue-400",
                            )}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            ></path>
                          </svg>
                        </div>
                        <h4
                          className={arClass(
                            "text-lg font-semibold mb-2 max-sm:text-sm",
                          )}
                        >
                          <Text>Upgrade Request Details</Text>
                        </h4>
                        <p
                          className={arClass(
                            `text-sm max-sm:text-xs ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            } mb-4`,
                          )}
                        >
                          <Text>
                            Submit your subscription upgrade request. Our team will
                            review your request and process it shortly.
                          </Text>
                        </p>
                      </div>

                      {/* Additional Information Field */}
                      <div>
                        <label
                          className={arClass(
                            "block text-sm font-medium mb-2 max-sm:text-xs",
                          )}
                        >
                          <Text>Additional Notes (Optional)</Text>
                        </label>
                        <textarea
                          rows="3"
                          placeholder={textTranslator(
                            "Any special requirements or questions...",
                            "SUBSCRIPTION",
                          )}
                          className={arClass(
                            `w-full px-4 py-3 rounded-lg border max-sm:text-xs ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-white border-gray-300"
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none`,
                          )}
                        />
                      </div>

                      {/* Terms and Conditions */}
                      <div className={arClass("flex items-start")}>
                        <input
                          type="checkbox"
                          id="terms"
                          className={arClass("mt-1 mr-2 max-sm:text-xs")}
                        />
                        <label
                          htmlFor="terms"
                          className={arClass("text-sm max-sm:text-xs")}
                        >
                          <Text>I confirm that this request is accurate and I authorize the upgrade</Text>
                        </label>
                      </div>

                      {/* Request Summary */}
                      <div
                        className={arClass(
                          `p-4 rounded-lg ${
                            darkMode ? "bg-gray-700" : "bg-gray-100"
                          }`,
                        )}
                      >
                        <h5
                          className={arClass("font-medium mb-2 max-sm:text-sm")}
                        >
                          <Text>Request Summary</Text>
                        </h5>
                        <ul className={arClass("flex flex-col gap-2 text-sm")}>
                          <li className={arClass("flex justify-between")}>
                            <span className={arClass("max-sm:text-xs")}>
                              <Text>Current Plan</Text>
                            </span>
                            <span className={arClass("max-sm:text-xs")}>
                              {getTranslatedPlanName(
                                userSubscription?.planName,
                              ) || "None"}
                            </span>
                          </li>
                          <li className={arClass("flex justify-between")}>
                            <span className={arClass("max-sm:text-xs")}>
                              <Text>Requested Plan</Text>
                            </span>
                            <span
                              className={arClass(
                                "font-semibold max-sm:text-xs",
                              )}
                            >
                              {selectedPlan.name}
                            </span>
                          </li>
                          <li className={arClass("flex justify-between")}>
                            <span className={arClass("max-sm:text-xs")}>
                              <Text>Billing Cycle</Text>:
                            </span>
                            <span className={arClass("max-sm:text-xs")}>
                              {isYearly ? (
                                <Text>Yearly</Text>
                              ) : (
                                <Text>Monthly</Text>
                              )}
                            </span>
                          </li>
                          <li
                            className={arClass(
                              "flex justify-between pt-2 border-t border-gray-300 dark:border-gray-600",
                            )}
                          >
                            <span
                              className={arClass(
                                "font-semibold max-sm:text-xs",
                              )}
                            >
                              <Text>Total</Text>:
                            </span>
                            <span
                              className={arClass("font-bold max-sm:text-xs")}
                            >
                              {selectedPlan.price !== null ? (
                                defaultLanguage !== "ar" ? (
                                  `${currency} ${
                                    isYearly
                                      ? selectedPlan.yearlyPrice
                                      : selectedPlan.price
                                  }`
                                ) : (
                                  `${
                                    isYearly
                                      ? selectedPlan.yearlyPrice
                                      : selectedPlan.price
                                  } ${currency}`
                                )
                              ) : (
                                <Text>Custom</Text>
                              )}
                            </span>
                          </li>
                        </ul>
                      </div>

                      {/* Information Message */}
                      <div
                        className={arClass(
                          `p-3 rounded-lg ${
                            darkMode
                              ? "bg-blue-900/20 border border-blue-800"
                              : "bg-blue-50 border border-blue-200"
                          }`,
                        )}
                      >
                        <p
                          className={arClass(
                            "text-sm flex items-start max-sm:text-xs",
                          )}
                        >
                          <svg
                            className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            ></path>
                          </svg>
                          <span className={arClass("max-sm:text-xs")}>
                            <Text>
                              Your request will be submitted to our backend system. Our team will review your upgrade request and process it as soon as possible.
                            </Text>
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Request Actions */}
                    <div className="mt-8 flex gap-3">
                      <button
                        onClick={() => setShowPaymentModal(false)}
                        className={arClass(
                          `flex-1 py-3 rounded-lg font-medium max-sm:text-xs ${
                            darkMode
                              ? "bg-gray-700 hover:bg-gray-600"
                              : "bg-gray-200 hover:bg-gray-300"
                          }`,
                        )}
                      >
                        <Text>Cancel</Text>
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handlePayment}
                        className={arClass(
                          "flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium",
                        )}
                      >
                        <span
                          className={arClass(
                            "flex items-center justify-center gap-2 max-sm:text-xs",
                          )}
                        >
                          <svg
                            className={arClass("w-4 h-4")}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            ></path>
                          </svg>
                          <Text>Submit Request</Text>
                        </span>
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
        )
      }
    </div>
  );
};

export default Subscription;
